import { isNil } from 'lodash-es'
import {
  BasisConvention,
  Collateral,
  CollateralIdentifierType,
  CollateralIndexDetails,
  CollateralStatus,
  CollateralType,
  CouponFrequency,
  Currency,
  Index,
  IndexLag,
  OriginalSource,
  ProductSector,
  RateType,
  SpreadsheetMapping,
  TradingType,
  ValidationIssue
} from 'api'
import { Locale, Translation } from 'locale'
import { gatherIssues, parseDate, parseEnum, parseNumber, parseString, SpreadsheetParseResult } from '../upload/model'
import { Decimal, Result } from 'utils'
import { createInvalidValueValidationIssue, Severity } from 'model'

interface CollateralWithPrice extends Collateral {
  cleanPrice: number | null
}

interface CollateralSpreadsheetParseRowResult {
  collateral: CollateralWithPrice
  parseIssues: ValidationIssue[]
}

export interface CollateralSpreadsheetParseResult extends SpreadsheetParseResult {
  collaterals: CollateralWithPrice[]
}

export function parseCollateralSpreadsheet(
  filename: string,
  rows: any[],
  spreadsheetMapping: SpreadsheetMapping,
  getProductSectorByName: (name: string) => ProductSector | null,
  getIndexByCode: (name: string) => Index | null
): CollateralSpreadsheetParseResult {
  const collateralIds: string[] = []
  const duplicateIds: string[] = []
  return rows.reduce<CollateralSpreadsheetParseResult>(
    (spreadsheetResult, row, rowIdx) => {
      const rowResult = parseRow(row, rowIdx + 1, spreadsheetMapping, getProductSectorByName, getIndexByCode)

      if (!isNil(rowResult.collateral.id) && collateralIds.includes(rowResult.collateral.id)) {
        duplicateIds.push(rowResult.collateral.id)
        spreadsheetResult.issues.push(
          createInvalidValueValidationIssue(
            rowResult.collateral.collateralIdentifierType === CollateralIdentifierType.ISIN ? 'ISIN' : 'CUSIP',
            `Duplicate value "${rowResult.collateral.id}"`,
            Severity.ERROR
          )
        )
      } else if (!isNil(rowResult.collateral.id)) {
        collateralIds.push(rowResult.collateral.id)
      }

      spreadsheetResult.issues.push(...rowResult.parseIssues)
      if (rowResult.collateral && rowResult.parseIssues.length === 0 && !duplicateIds.includes(rowResult.collateral.id)) {
        spreadsheetResult.collaterals.push(rowResult.collateral)
      }

      return spreadsheetResult
    },
    { filename, rows, issues: [], collaterals: [] }
  )
}

function parseRow(
  row: any,
  rowNum: number,
  spreadsheetMapping: SpreadsheetMapping,
  getProductSectorByName: (name: string) => ProductSector | null,
  getIndexByCode: (name: string) => Index | null
): CollateralSpreadsheetParseRowResult {
  const tradingStatusResult = parseEnum<CollateralStatus>(
    row,
    rowNum,
    spreadsheetMapping.tradingStatus,
    spreadsheetMapping.tradingStatus.values,
    Locale.translate(Translation.TC_TRADING),
    false,
    true
  )
  const platformResult = parseEnum<CollateralIdentifierType>(
    row,
    rowNum,
    spreadsheetMapping.platform,
    spreadsheetMapping.platform.values,
    Locale.translate(Translation.TC_PLATFORM),
    false,
    true
  )
  const shortIdResult = parseString(row, rowNum, spreadsheetMapping.shortId, Locale.translate(Translation.TC_SHORT_ID), true)
  const isinResult = parseString(row, rowNum, spreadsheetMapping.isin, Locale.translate(Translation.TC_ISIN), true)
  const fisnResult = parseString(row, rowNum, spreadsheetMapping.fisn, Locale.translate(Translation.TC_FISN), true)
  const cfiResult = parseString(row, rowNum, spreadsheetMapping.cfi, Locale.translate(Translation.TC_CFI), true)
  const cusipResult = parseString(row, rowNum, spreadsheetMapping.cusip, Locale.translate(Translation.TC_CUSIP), true)
  const currencyResult = parseEnum<Currency>(
    row,
    rowNum,
    spreadsheetMapping.currency,
    spreadsheetMapping.currency.values,
    Locale.translate(Translation.TC_CURRENCY),
    false,
    true
  )
  const countryResult = parseString(row, rowNum, spreadsheetMapping.country, Locale.translate(Translation.TC_COUNTRY), true)
  const sectorResult = parseString(row, rowNum, spreadsheetMapping.sector, Locale.translate(Translation.TC_SECTOR), true)
  const tradingTypeResult = parseEnum<TradingType>(
    row,
    rowNum,
    spreadsheetMapping.tradingType,
    spreadsheetMapping.tradingType.values,
    Locale.translate(Translation.TC_TRADING_TYPE),
    false,
    true
  )
  const cleanPriceResult = parseNumber(row, rowNum, spreadsheetMapping.cleanPrice, Locale.translate(Translation.TC_CLEAN_PRICE), true)
  const ticketSizeResult = parseNumber(row, rowNum, spreadsheetMapping.ticketSize, Locale.translate(Translation.TC_TICKET_SIZE), true)
  const issuerResult = parseString(row, rowNum, spreadsheetMapping.issuer, Locale.translate(Translation.TC_ISSUER), true)
  const firstSettlementDateResult = parseDate(
    row,
    rowNum,
    spreadsheetMapping.firstSettlementDate,
    Locale.translate(Translation.TC_FIRST_SETTLEMENT_DATE),
    true
  )
  const accrualDateResult = parseDate(row, rowNum, spreadsheetMapping.accrualDate, Locale.translate(Translation.TC_ACCRUAL_DATE), true)
  const firstCouponDateResult = parseDate(
    row,
    rowNum,
    spreadsheetMapping.firstCouponDate,
    Locale.translate(Translation.TC_FIRST_COUPON_DATE),
    true
  )
  const maturityDateResult = parseDate(row, rowNum, spreadsheetMapping.maturityDate, Locale.translate(Translation.TC_MATURITY_DATE), true)
  const couponTypeResult = parseEnum<RateType>(
    row,
    rowNum,
    spreadsheetMapping.couponType,
    spreadsheetMapping.couponType.values,
    Locale.translate(Translation.TC_COUPON_TYPE),
    false,
    true
  )
  const basisConventionResult = parseEnum<BasisConvention>(
    row,
    rowNum,
    spreadsheetMapping.basisConvention,
    spreadsheetMapping.basisConvention.values,
    Locale.translate(Translation.TC_BASIS_CONVENTION),
    true
  )
  const couponResult = parseNumber(row, rowNum, spreadsheetMapping.coupon, Locale.translate(Translation.TC_COUPON), true)
  const frequencyResult = parseEnum<CouponFrequency>(
    row,
    rowNum,
    spreadsheetMapping.frequency,
    spreadsheetMapping.frequency.values,
    Locale.translate(Translation.TC_FREQUENCY),
    false,
    true
  )
  const collateralTypeResult = parseEnum<CollateralType>(
    row,
    rowNum,
    spreadsheetMapping.collateralType,
    spreadsheetMapping.collateralType.values,
    Locale.translate(Translation.TC_COLLATERAL_TYPE),
    false,
    true
  )
  const indexResult = parseString(row, rowNum, spreadsheetMapping.index, Locale.translate(Translation.TC_INDEX), true)
  const lagResult = parseEnum<IndexLag>(
    row,
    rowNum,
    spreadsheetMapping.lag,
    spreadsheetMapping.lag.values,
    Locale.translate(Translation.TC_INDEX_LAG),
    false,
    true
  )
  const baseIndexValueResult = parseNumber(
    row,
    rowNum,
    spreadsheetMapping.baseIndexValue,
    Locale.translate(Translation.TC_BASE_INDEX_VALUE),
    true
  )

  const parseIssues = gatherIssues(
    tradingStatusResult,
    platformResult,
    shortIdResult,
    isinResult,
    fisnResult,
    cfiResult,
    cusipResult,
    currencyResult,
    countryResult,
    tradingTypeResult,
    cleanPriceResult,
    ticketSizeResult,
    issuerResult,
    firstSettlementDateResult,
    accrualDateResult,
    firstCouponDateResult,
    maturityDateResult,
    couponTypeResult,
    basisConventionResult,
    couponResult,
    frequencyResult,
    collateralTypeResult,
    indexResult,
    lagResult,
    baseIndexValueResult
  )

  const platform = platformResult.isValue ? platformResult.value : null
  const isin = isinResult.isValue ? isinResult.value.toUpperCase() : null
  const cusip = cusipResult.isValue ? cusipResult.value.toUpperCase() : null
  const id = platform === CollateralIdentifierType.ISIN ? isin : platform === CollateralIdentifierType.CUSIP ? cusip : null

  const productSectorId = getProductSectorId(sectorResult, getProductSectorByName)
  const collateralIndexDetails = getCollateralIndexDetails(
    collateralTypeResult,
    indexResult,
    lagResult,
    baseIndexValueResult,
    getIndexByCode
  )

  if (isNil(id)) {
    const idType =
      platform === CollateralIdentifierType.ISIN
        ? Locale.translate(Translation.TC_ISIN)
        : platform === CollateralIdentifierType.CUSIP
        ? Locale.translate(Translation.TC_CUSIP)
        : Locale.translate(Translation.TC_ISIN_CUSIP)
    parseIssues.push(createInvalidValueValidationIssue(idType, 'A value is required', Severity.ERROR))
  }

  if (isNil(productSectorId) && sectorResult.isValue) {
    parseIssues.push(
      createInvalidValueValidationIssue(
        Locale.translate(Translation.TC_SECTOR),
        `Cannot find product sector with name "${sectorResult.value}"`,
        Severity.ERROR
      )
    )
  }
  if (
    collateralTypeResult.isValue &&
    collateralTypeResult.value === CollateralType.INDEX_LINKED &&
    isNil(collateralIndexDetails?.indexId) &&
    indexResult.isValue
  ) {
    parseIssues.push(
      createInvalidValueValidationIssue(
        Locale.translate(Translation.TC_INDEX),
        `Cannot find index with code "${indexResult.value}"`,
        Severity.ERROR
      )
    )
  }

  return {
    parseIssues,
    collateral: {
      id: id!,
      status: tradingStatusResult.isValue ? tradingStatusResult.value : CollateralStatus.DISABLED,
      collateralIdentifierType: platform!,
      name: shortIdResult.isValue ? shortIdResult.value : 'TBC',
      isin: isin,
      fisin: fisnResult.isValue ? fisnResult.value : 'TBC',
      cfi: cfiResult.isValue ? cfiResult.value : 'XXXXXX',
      cusip: cusip,
      currency: currencyResult.isValue ? currencyResult.value : null!,
      countryCode: countryResult.isValue ? countryResult.value : null!,
      productSectorId: productSectorId!,
      tradingType: tradingTypeResult.isValue ? tradingTypeResult.value : null!,
      ticketIncrement: ticketSizeResult.isValue ? ticketSizeResult.value : null!,
      issuer: issuerResult.isValue ? issuerResult.value : null!,
      issueDate: firstSettlementDateResult.isValue ? firstSettlementDateResult.value : null!,
      dirtyPrice: null!,
      couponAccruedInterestDate: accrualDateResult.isValue ? accrualDateResult.value : null,
      couponFirstDate: firstCouponDateResult.isValue ? firstCouponDateResult.value : null,
      couponMaturityDate: maturityDateResult.isValue ? maturityDateResult.value : null,
      couponType: couponTypeResult.isValue ? couponTypeResult.value : null,
      basisConvention: basisConventionResult.isValue ? basisConventionResult.value : null!,
      coupon: couponResult.isValue ? Decimal.shift(couponResult.value, -2) : null!,
      couponFrequency: frequencyResult.isValue ? frequencyResult.value : null,
      collateralType: collateralTypeResult.isValue ? collateralTypeResult.value : null!,
      collateralIndexDetails,
      cleanPrice: cleanPriceResult.isValue ? cleanPriceResult.value : null,
      prospectus: null,
      lookupFailedReason: null,
      mockStrategy: null,
      firstTradeTimestamp: null,
      indexSpread: null,
      issuerLei: null,
      seniority: null,
      totalIssuedNominalAmount: null,
      originalSource: OriginalSource.FILE_UPLOAD,
      ric: null,
      lastUpdatedAtByUser: null,
      lastUpdatedAtByUserOrSystem: null,
      lastUpdatedByUser: null,
      assetType: null,
      countryOfIncorporation: null,
      countryOfIssuance: null,
      countryOfRiskAll: null,
      countryOfRiskPrimary: null,
      countryOfRiskUltimateParentAll: null,
      countryOfRiskUltimateParentPrimary: null,
      minimumTicketSize: 1,
      domicile: null,
      factor: null,
      factorDate: null,
      complianceOverride: null,
      rule144aIndicator: false,
      eeaVenueEligible: null,
      inEsmaFirds: null,
      issuerOrgId: null,
      ukVenueEligible: null,
      assetStatus: null,
      assetStatusEffectiveDate: null,
      micReportDate: null,
      issue: null,
      assetSubType: null,
      debtType: null,
      governmentBondType: null,
      refinitivAssetType: null,
      refinitivBaseIndex: null,
      mbsPriceDate: null,
      euCompliant: true
    }
  }
}

function getProductSectorId(sectorResult: Result<string>, getProductSectorByName: (name: string) => ProductSector | null): string | null {
  if (!sectorResult.isValue) {
    return null
  }

  const productSector = getProductSectorByName(sectorResult.value)
  return productSector ? productSector.id : null
}

function getCollateralIndexDetails(
  collateralTypeResult: Result<CollateralType>,
  indexResult: Result<string>,
  lagResult: Result<IndexLag>,
  baseIndexValueResult: Result<number>,
  getIndexByCode: (name: string) => Index | null
): CollateralIndexDetails | null {
  if (collateralTypeResult.isValue && collateralTypeResult.value === CollateralType.STANDARD) {
    return null
  }

  const index = indexResult.isValue ? getIndexByCode(indexResult.value) : null
  return {
    indexId: index?.id ?? null,
    indexLag: lagResult.isValue ? lagResult.value : null,
    baseIndexValue: baseIndexValueResult.isValue ? baseIndexValueResult.value : null
  }
}
